import type { PassengerFieldNames } from '@/hooks/usePersonFieldRenderer';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import type { PassengerDTO } from '@/types/dto/agent';
import type { BookingPassenger, CheckoutPassenger } from '@/types/passenger';
import { PassengerGenderOptions } from '@/types/passenger';
import { ProfileGenderOptions } from '@/types/profile';
import isEmpty from 'lodash/isEmpty';

type PassengerCard = NonNullable<PassengerDTO['cards']>[number];

export const formatPassengerCard = (
  card: PassengerCard
): string | undefined => {
  const label = card.name || card.code;

  if (!label && !card.number) {
    return undefined;
  }

  if (label && card.number) {
    return `${label}: ${card.number}`;
  }

  return label || card.number;
};

export type PrmNeedCode = Extract<
  TransTextKeys,
  'WHEELCHAIR' | 'WHEELCHAIR_AND_SEAT' | 'EASY_ACCESS'
>;

const PRM_NEED_CODES: ReadonlySet<PrmNeedCode> = new Set([
  'WHEELCHAIR',
  'WHEELCHAIR_AND_SEAT',
  'EASY_ACCESS',
]);

export const isPrmNeedCode = (need: string): need is PrmNeedCode =>
  PRM_NEED_CODES.has(need as PrmNeedCode);

export const getPassengerName = (
  { firstName, lastName }: Partial<CheckoutPassenger>,
  index: number = 0
) => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return isEmpty(fullName) ? (
    <TransText i18nKey="passengerIndex" values={{ count: index + 1 }} />
  ) : (
    fullName
  );
};

export const profileGenderToPassengerGenderMap: Record<
  ProfileGenderOptions,
  PassengerGenderOptions
> = {
  [ProfileGenderOptions.MALE]: PassengerGenderOptions.MALE,
  [ProfileGenderOptions.FEMALE]: PassengerGenderOptions.FEMALE,
  [ProfileGenderOptions.X]: PassengerGenderOptions.X,
};

export const getPassengerGenderFromProfileGender = (
  gender?: ProfileGenderOptions | string
) =>
  gender && gender in profileGenderToPassengerGenderMap
    ? profileGenderToPassengerGenderMap[gender as ProfileGenderOptions]
    : undefined;

// Maps a booking passenger onto the configurable form field values, hydrating
// the address/company fields so both checkout and manage-flow edit show what is
// already stored. The Record<PassengerFieldNames, string> return type forces
// every field to be covered, so adding a field can't silently miss a call site.
export const bookingPassengerToFormValues = (
  passenger: BookingPassenger
): Record<PassengerFieldNames, string> => ({
  firstName: passenger.firstName ?? '',
  lastName: passenger.lastName ?? '',
  email: passenger.contactInformation?.emailAddress ?? '',
  phoneNumber: passenger.contactInformation?.phoneNumber ?? '',
  birthDate: passenger.birthDate ?? '',
  gender: passenger.gender ?? '',
  zipCode: passenger.address?.zipCode ?? '',
  stateCode: passenger.address?.stateCode ?? '',
  companyUnitCode: passenger.companyDetails?.unitCode ?? '',
});
